export type OutreachStatus =
  | 'PENDING_SEAT'
  | 'PENDING_PROVISIONING'
  | 'AWAITING_SENDER'
  | 'AWAITING_CAMPAIGN'
  | 'READY'
  | 'PAUSED'
  | 'PAUSED_DUE_TO_BUDGET'
  | 'ERROR'
  | 'DISCONNECTING'
  | 'DISCONNECTED'

/**
 * Outreach statuses that unlock the full investor matches list without a paid
 * entitlement. Any startup that has progressed past AWAITING_SENDER qualifies.
 *
 * ⚠ KEEP IN SYNC with apps/backend/src/constants/outreach.ts
 * Both files define this set independently. If you add or remove a status here,
 * update the backend copy too — otherwise the backend gate and the UI lock
 * state will disagree.
 */
export const OUTREACH_UNLOCK_STATUSES = new Set<OutreachStatus>([
  'AWAITING_CAMPAIGN',
  'READY',
  'PAUSED',
  'PAUSED_DUE_TO_BUDGET',
  'ERROR',
  'DISCONNECTING',
  'DISCONNECTED',
])

export interface OutreachStatusData {
  status: OutreachStatus | null
  invitationUrl?: string
  invitationStatus?: 'Pending' | 'Accepted' | 'Expired' | null
  heyReachWorkspaceId?: number | null
  heyReachCampaignId?: number | null
  lastError?: string | null
  queuedCount?: number
  pushedCount?: number
  connectionCount?: number
  replyCount?: number
  heyReachWorkspaceName?: string | null
  /** ISO timestamp once the founder has approved msg1/2/3; null/absent before. */
  founderApprovedAt?: string | null
  /** ISO timestamp once an admin confirmed HeyReach sending limits are set. */
  limitsUpdatedAt?: string | null
  /** ISO timestamp once the founder confirmed their messages look good (review gate). */
  messagesReviewedAt?: string | null
  /** ISO timestamp once the founder chose which campaigns to run (Choose campaigns gate). */
  channelsSelectedAt?: string | null
  /**
   * The single direct-investor channel flag: true ⇒ running (Pause shown),
   * false ⇒ off/paused (Resume shown). Pause/Resume + Choose Campaigns toggle it.
   */
  directInvestorOutreachEnabled?: boolean | null
  /** ISO timestamp once the founder requested disconnect; drives the blocking screen. */
  disconnectRequestedAt?: string | null
  /** ISO timestamp when the free trial ends; charges before it are waived. Null pre-launch. */
  trialEndsAt?: string | null
}

/** Admin-only HeyReach campaign/sender/invitation debug fields for a startup. */
export interface OutreachHeyReachDebug {
  heyReachCampaignId: number | null
  heyReachSenderId: number | null
  heyReachInvitationId: number | null
  invitedEmail: string | null
  invitationUrl: string | null
}

export interface OutreachMessagesData {
  msg1: string | null
  // Each message is a self-contained template carrying its greeting, link frame
  // + [Link] token, and (for msg2) its own closing CTA inline.
  msg2: string | null
  msg3: string | null
  // Legacy (pre-SF-121-v2) msg2 CTA, kept in a separate field; the founder
  // preview folds it into the msg2 decoration. Null/absent for v2 rows.
  msg2Cta?: string | null
  // Langfuse prompt version per message; drives legacy-vs-v2 preview rendering
  // (mirrors the backend push). "<n>-custom" once hand-edited.
  msg1PromptVersion?: string | null
  msg2PromptVersion?: string | null
  msg3PromptVersion?: string | null
  founderApprovedAt: string | null
  messagesReviewedAt: string | null
  /**
   * Every live version of each message. `arm` is the identity: "control" is the
   * base prompt, "arm-N" an A/B sibling, "custom" the founder's own edit — which,
   * when present, is the only thing sent for that field.
   */
  variants?: {
    msg1: OutreachMessageVariant[]
    msg2: OutreachMessageVariant[]
    msg3: OutreachMessageVariant[]
  }
}

/** One stored version of one outreach message. */
export interface OutreachMessageVariant {
  id: string
  arm: string
  text: string
  promptVersion: string | null
}

export interface AdminPendingOutreachItem {
  id: string
  startupId: string
  startupName: string
  workspaceId: number | null
  workspaceName: string
  createdAt: string
}

export interface AdminLimitsPendingItem {
  id: string
  startupId: string
  startupName: string
  workspaceId: number | null
  workspaceName: string
  campaignId: number | null
  createdAt: string
}

export interface AdminStuckAwaitingSenderItem {
  id: string
  startupId: string
  startupName: string
  workspaceId: number | null
  workspaceName: string
  invitedEmail: string
  awaitingSenderSince: string | null
  createdAt: string
}

export interface AdminOutreachStartupMetricsItem {
  startupId: string
  startupName: string
  prescreenScore: number | null
  status: string
  since: string
  matches: number
  /** Head of the match list — what outreach actually works, top-first. */
  p90RerankScore: number | null
  p50RerankScore: number | null
  /** Contacts per channel, with the subset no longer in flight (finished or replied). */
  directContacts: number
  directContactsPlanned: boolean
  directFinished: number
  portfolioContacts: number
  portfolioFinished: number
  /**
   * Investor-side interactions only, matching the founder hero's "Interactions
   * so far": accepts, replies, calls booked, warm intros and data-room views.
   * Excludes HeyReach's VIEWED_PROFILE — that is our own sender warming up the
   * lead, so it fires for every contact we push.
   */
  engagement: number
  /**
   * Contacts materialized but still awaiting a per-contact founder approval.
   * Nothing is pushed until a contact is approved, so a READY startup with
   * everything pending sits at 0% progress with no error.
   */
  directAwaitingApproval: number
  portfolioAwaitingApproval: number
}

export interface AdminSeatOccupantItem {
  id: string
  startupId: string
  startupName: string
  status: string
  invitedEmail: string
  occupiedAt: string
}

export interface AdminSeatQueueResponse {
  activeItems: AdminSeatOccupantItem[]
  activeCount: number
}

export interface OutreachQueueItem {
  leadId: string
  /**
   * Lifecycle state. The board (queue) returns
   * awaiting/approved/in_progress/engaged/finished; the Archive returns
   * skipped/declined/engaged/finished.
   *
   * `skipped` and `declined` are both off the board, but they are not the same
   * thing: declined is a judgement we keep for future targeting, skipped is
   * "not now" and stays restorable.
   */
  status: 'awaiting' | 'approved' | 'in_progress' | 'engaged' | 'finished' | 'declined' | 'skipped'
  /**
   * Why a `finished` contact stopped: `fund_engaged` = another contact at the
   * same fund replied / booked a call / rejected; `exhausted` = this contact's
   * own sequence ran end-to-end with no reply. Only set on `finished` items.
   */
  finishedReason?: 'exhausted' | 'fund_engaged' | null
  /**
   * Flow progress — drives the in-progress sequence stepper and the engaged
   * row's reply info. Present on `in_progress` and `engaged` items.
   * `sentAt`/`acceptedAt`/`repliedAt` are ISO timestamps (or null).
   */
  progress?: {
    viewedProfileAt: string | null
    likedPostAt: string | null
    connectionRequestSentAt: string | null
    sentAt: string | null
    acceptedAt: string | null
    repliedAt: string | null
    replySummary: string | null
    callBooked: boolean
    views: number
    returnVisits: number
    /** The message row behind this lead — what gets marked read. */
    messageId?: string | null
    /** Null while the founder still has this reply to read. */
    seenAt?: string | null
  } | null
  contact: { name: string; title: string | null; linkedinUrl: string | null; scope: string | null }
  investor: {
    id: string
    name: string
    description: string | null
    fitScore: number | null
    stages: string[]
    sectors: string[]
    geographies: string[]
    minTicket: number | null
    maxTicket: number | null
    reasoning: string | null
    portfolio: string[]
  }
  messages: {
    msg1: string | null
    msg2: string | null
    msg3: string | null
    // Per-startup Langfuse prompt version per message; drives legacy-vs-v2 preview.
    msg1PromptVersion?: string | null
    msg2PromptVersion?: string | null
    msg3PromptVersion?: string | null
    // Present once the startup has A/B variants; the preview shows one of them.
    variants?: {
      msg1: OutreachMessageVariant[]
      msg2: OutreachMessageVariant[]
      msg3: OutreachMessageVariant[]
    }
  }
}

export interface AdminWithdrawalRequestItem {
  id: string
  startupId: string
  startupName: string
  amountCents: number
  createdAt: string
}

export interface AdminCallCandidateItem {
  messageId: string
  startupId: string
  startupName: string
  workspaceId: number | null
  workspaceName: string
  investorName: string
  contactName: string
  replySummary: string | null
  rejectionReason: string | null
}

/** One (startup, field, arm) cell of the copy-arm readout. */
export interface OutreachArmCell {
  startupId: string
  startupName: string
  field: 'msg1' | 'msg2' | 'msg3'
  arm: string
  sent: number
  accepted: number
  replied: number
}

export interface OutreachArmReadoutData {
  cells: OutreachArmCell[]
  exclusions: {
    unassigned: number
    custom: number
    fallbackModel: number
  }
}

/** Portfolio outreach messages, with their live A/B versions when present. */
export interface PortfolioMessagesData {
  msg1: string | null
  msg2: string | null
  msg3: string | null
  variants?: {
    msg1: OutreachMessageVariant[]
    msg2: OutreachMessageVariant[]
    msg3: OutreachMessageVariant[]
  }
}

/** Every live message variant for a startup, both campaigns. Admin view. */
export interface AdminOutreachVariants {
  investor: Record<'msg1' | 'msg2' | 'msg3', OutreachMessageVariant[]>
  portfolio: Record<'msg1' | 'msg2' | 'msg3', OutreachMessageVariant[]>
}
